import type { Entry } from "contentful";
import type { HeadersFunction } from "react-router";
import { data, useLoaderData, useSearchParams } from "react-router";

import { DEFAULT_SWR_CACHE_CONTROL, SWR } from "~/.server/caching.server";
import { getContext } from "~/.server/context.server";
import { getShopperContext } from "~/.server/shopper-context.server";
import { getTimingContext } from "~/.server/timing.server";
import { getClient } from "~/contentful/contentful.server";
import { getHeroContentMainImageHeaderLink } from "~/contentful/hero.server";
import { deferDataRequest } from "~/lib/utils-server/http.server";
import { getHomeMeta } from "~/seo/servers/seo.home.server";
import { productSearch } from "~/shop/products/productSearch.server";

import { useContentfulLiveUpdates } from "@contentful/live-preview/react";

import { H3 } from "~/components/ui/text";
import {
  type TypeHeroSectionSkeleton,
  type TypeHeroSlotSkeleton,
  type TypeHomePageContentSkeleton,
  type TypeProductRecommendationsProductIDsSkeleton,
  isTypeBannerWithLinks,
} from "~/contentful/compiled";
import { ContentBanner } from "~/contentful/components/content-banner";
import { HeroSection } from "~/contentful/components/hero-section";
import { HomePageHero } from "~/contentful/components/home-page-hero";
import { HomePageSections } from "~/contentful/components/home-page-sections";
import { HomePageSlot } from "~/contentful/components/home-page-slot";
import { HOME_PAGE_CONTENT } from "~/contentful/contents";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { ProductRecommendations } from "~/routes/product-details/components/products-recommendations";

import { createRecommendationData } from "./resources/components/added-to-basket-flyout";

export const handle: AppHandle = {
  meganav: ({ heroSlotData: contentData }: SerializeFrom<typeof loader>) => {
    const themeData = contentData?.items?.[0];
    if (!themeData) return { variant: "dark" };
    return {
      variant: themeData ? themeData.fields.navigationTextColor : "dark",
    };
  },
  contentRootHeroSlot({ heroSlotData, preview }: SerializeFrom<typeof loader>) {
    const contentData = heroSlotData;
    const data = contentData?.items?.[0];
    const contentType = data?.fields.heroContent?.sys.contentType.sys.id;
    return (
      <div
        className={cn(
          "relative flex overflow-hidden",
          contentType !== "textBlock" && "mt-nav-adjust",
        )}
      >
        <HeroSection originalContent={heroSlotData} preview={preview} />
      </div>
    );
  },
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    "cache-control":
      loaderHeaders.get("cache-control") ??
      "s-maxage=600, stale-while-revalidate",
  };
};
export const loader = async ({
  request,
  context: { waitUntil, locale },
}: LoaderArgs) => {
  /* Caching template */
  const [swr] = SWR.stable(
    "homepage-heroSlot-data",
    ["homepage", "heroSlot", "data", "contentful"],
    DEFAULT_SWR_CACHE_CONTROL,
  );
  const [swrContent] = SWR.stable(
    "homepage-index-content",
    ["homepage", "index", "contentful"],
    DEFAULT_SWR_CACHE_CONTROL,
  );
  const [swrRecommendation] = SWR.stable(
    "recommendation-search",
    ["recommendation", "search", "data"],
    DEFAULT_SWR_CACHE_CONTROL,
  );
  const { time } = getTimingContext();

  const sourceCode = getShopperContext()?.sourceCode;

  const addToCartRecommendation = createRecommendationData(() =>
    swrRecommendation(
      () =>
        productSearch({
          refine: ["cgid=root"],
          limit: 10,
        }).then(data => {
          return data.hits ?? [];
        }),
      ["addToCartRecommendation", sourceCode],
    ),
  );

  const url = new URL(request.url);
  const preview = !!url.searchParams.get("preview");
  const client = getClient(preview);

  /* this handles index being called for all non-locale urls e.g. /something-that-does-not-exist  */
  if (
    getContext().locale.id === "default" &&
    new URL(request.url).pathname !== "/"
  ) {
    throw new Response("Not Found", { status: 404 });
  }

  const heroSlotData = swr(async () => {
    const slugs = ["homepage-example"];
    if (sourceCode) {
      slugs.push(`${slugs[0]}-${sourceCode}`);
    }
    return await client
      .getEntries<TypeHeroSectionSkeleton>({
        content_type: "heroSection",
        "fields.slug[in]": slugs,
        include: 10,
      })
      .then(res => {
        // do we have with source code?
        if (res.items.find(item => item.fields.slug === slugs[1])) {
          res.items = res.items.filter(item => item.fields.slug === slugs[1]);
          return res;
        }
        return res;
      });
  }, ["homepage", sourceCode]);

  const content = await swrContent(async () => {
    return await client.getEntry<TypeHomePageContentSkeleton>(
      HOME_PAGE_CONTENT,
      { include: 10 },
    );
  }, []);

  /* For GA4 */
  const analytics = {
    action: "page_view",
    category: "home_page",
    label: "home_page",
    value: "home_page",
  };
  const [seo] = await time(
    deferDataRequest(
      Promise.resolve(content).then(data => {
        const seoData = data.fields.seo;
        return getHomeMeta({
          type: "home",
          options: {
            url: request.url,
            h1: seoData?.fields.h1,
            title: seoData?.fields.title,
            description: seoData?.fields.description,
            lang: locale.alias,
          },
          robots: {
            noFollow: seoData?.fields.followLinks === "nofollow" ? true : false,
            noIndex:
              seoData?.fields.indexedBySearchEngines === "noindex"
                ? true
                : false,
          },
          og: {
            type: seoData?.fields.ogtype,
            title: seoData?.fields.ogtitle,
            description: seoData?.fields.ogdescription,
            url: seoData?.fields.ogurl,
            image: seoData?.fields.ogimage,
          },
        });
      }),
    ),
    {
      type: "seo",
      desc: "SEO",
    },
  );
  return data(
    {
      heroSlotData: await heroSlotData,
      ...addToCartRecommendation,
      content,
      analytics,
      seo,
      preview,
    },
    {
      headers: {
        "Cache-Control": "s-maxage=600, stale-while-revalidate",
        ...getHeroContentMainImageHeaderLink(await heroSlotData),
      },
    },
  );
};

export default function IndexPage() {
  const { content } = useLoaderData<typeof loader>();
  const liveContent = useContentfulLiveUpdates(content);
  const [searchParams] = useSearchParams();
  const preview = !!searchParams.get("preview");
  const { fields } = preview ? liveContent : content;

  return (
    <>
      <section className="">
        <h1 className="hidden">Home page</h1>
        <div className="container mx-auto flex flex-wrap items-center pb-12 pt-4">
          <nav id="store" className="top-0 w-full px-6 py-1">
            {fields.section3Blocks && (
              <HomePageSections
                sections={[fields.section3Blocks]}
                className="mb-8"
                fixedHeight={true}
              />
            )}
            {fields.heroSlot && (
              <div className="relative mb-8 w-full overflow-hidden">
                <HomePageHero
                  content={
                    fields.heroSlot as Entry<
                      TypeHeroSlotSkeleton,
                      "WITHOUT_UNRESOLVABLE_LINKS",
                      string
                    >
                  }
                  className="h-full w-full"
                />
              </div>
            )}
            {fields.recommendations && (
              <div
                className="contentful-product-recommendations mb-8 px-6 lg:px-10 xl:px-24"
                key={`recommendations-${fields.recommendations.sys.id}`}
              >
                <ProductRecommendations
                  sysId={fields.recommendations.sys.id}
                  introspectId={fields.recommendations.sys.id}
                  fields={
                    fields.recommendations.fields as Entry<
                      TypeProductRecommendationsProductIDsSkeleton,
                      "WITHOUT_UNRESOLVABLE_LINKS",
                      string
                    >["fields"]
                  }
                />
              </div>
            )}
            {fields.section2Blocks && (
              <HomePageSections
                sections={[fields.section2Blocks]}
                className="mb-8"
                fixedHeight={true}
              />
            )}
            <H3 className="px-2 pb-5 text-2xl font-semibold">
              {fields.slotsTitle}
            </H3>
            <div className="relative mx-auto mb-4 flex flex-wrap justify-between">
              {fields.spotMedia &&
                fields.spotMedia.map(slot => (
                  <HomePageSlot key={slot?.sys.id} content={slot} />
                ))}
            </div>

            <H3 className="px-2 pb-5 text-2xl font-semibold">
              {fields.bannersTitle}
            </H3>
            {fields.banners &&
              fields.banners.map(banner =>
                banner &&
                isTypeBannerWithLinks<"WITHOUT_UNRESOLVABLE_LINKS", string>(
                  banner,
                ) ? (
                  <div key={banner?.sys.id} className="w-full px-2">
                    <ContentBanner content={banner} />
                  </div>
                ) : null,
              )}
          </nav>
        </div>
      </section>
    </>
  );
}
